import * as Sentry from '@sentry/vue';
import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouterScrollBehavior,
} from 'vue-router';
import TopPage from '@/components/pages/TopPage/Index.vue';
import MetadataService from '@/api/MetadataService';
import AuthenticationService, { RefreshIndexPost } from '@/services/AuthenticationService';

const routes: Array<RouteRecordRaw> = [
  {
    // トップ
    path: '/',
    name: 'top',
    component: TopPage,
  },
  {
    // 初めての方へ
    path: '/first',
    name: 'first',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: '初めての方へ',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwFirst.vue'),
      },
    ],
  },
  {
    // ヘルプ
    path: '/help',
    name: 'help',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: 'ヘルプ',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwHelp.vue'),
      },
    ],
  },
  {
    // 利用規約
    path: '/terms',
    name: 'terms',
    component: () => import('@/components/pages/TermsPage/Index.vue'),
    meta: {
      title: '利用規約',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/TermsPage/SwTerms.vue'),
      },
    ],
  },
  {
    // 投稿ガイドライン
    path: '/guidelines',
    name: 'guidelines',
    component: () => import('@/components/pages/TermsPage/Index.vue'),
    meta: {
      title: '投稿ガイドライン',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/TermsPage/SwGuidelines.vue'),
      },
    ],
  },
  {
    // プライバシーポリシー
    path: '/privacypolicy',
    name: 'privacypolicy',
    component: () => import('@/components/pages/TermsPage/Index.vue'),
    meta: {
      title: 'プライバシーポリシー',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/TermsPage/SwPrivacyPolicy.vue'),
      },
    ],
  },
  {
    // 運営会社
    path: '/company',
    name: 'company',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: '運営会社',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwCompany.vue'),
      },
    ],
  },
  {
    // お知らせ一覧
    path: '/informations',
    name: 'informations',
    component: () => import('@/components/pages/InformationListPage/Index.vue'),
    meta: {
      title: 'お知らせ一覧',
      description: '',
    },
  },
  {
    // お知らせ詳細
    path: '/informations/:informationId',
    name: 'informationDetail',
    props: true,
    component: () => import('@/components/pages/InformationDetailPage/Index.vue'),
    meta: {
      title: 'お知らせ詳細',
      description: '',
    },
  },
  {
    // お問い合わせ
    path: '/inquiry',
    name: 'inquiry',
    component: () => import('@/components/pages/InquiryPage/Index.vue'),
    meta: {
      title: 'お問い合わせ',
      description: '',
    },
  },
  {
    // ログイン
    path: '/signin',
    name: 'signin',
    component: () => import('@/components/pages/SigninPage/Index.vue'),
    meta: {
      title: 'ログイン',
      description: '',
    },
  },
  {
    // ログアウト
    path: '/signout',
    name: 'signout',
    component: TopPage,
    meta: {
      title: '',
      description: '',
    },
  },
  {
    // パスワードリマインダー
    path: '/password_reminder',
    name: 'passwordReminder',
    component: () => import('@/components/pages/PasswordReminderPage/Index.vue'),
    meta: {
      title: 'パスワードをお忘れの方',
      description: '',
    },
  },
  {
    // パスワードリマインダー
    path: '/reset_password',
    name: 'resetPassword',
    component: () => import('@/components/pages/ResetPasswordPage/Index.vue'),
    meta: {
      title: 'パスワードをお忘れの方',
      description: '',
    },
  },
  {
    // アカウント登録
    path: '/signup',
    name: 'signup',
    component: () => import('@/components/pages/SignupPage/Index.vue'),
    meta: {
      title: 'アカウント登録',
      description: '',
    },
  },
  {
    // アカウント登録（プロフィール入力）
    path: '/register',
    name: 'register',
    component: () => import('@/components/pages/RegisterProfilePage/Index.vue'),
    meta: {
      title: 'プロフィール登録',
      description: '',
    },
  },
  {
    // 作品検索
    path: '/works',
    name: 'searchWorkspage',
    component: () => import('@/components/pages/SearchWorksPage/Index.vue'),
    meta: {
      title: '投稿作品を検索',
      description: '',
    },
  },
  {
    // 作品詳細
    path: '/works/:workId',
    name: 'workDetail',
    props: true,
    component: () => import('@/components/pages/WorkDetailPage/Index.vue'),
    meta: {
      title: '投稿作品',
      description: '',
    },
  },
  {
    // 作者詳細
    path: '/users/:userId',
    name: 'userDetail',
    props: true,
    component: () => import('@/components/pages/UserDetailPage/Index.vue'),
    meta: {
      title: '作者詳細',
      description: '',
    },
  },
  {
    // マイページ
    path: '/mypage',
    name: 'mypage',
    component: () => import('@/components/pages/MyPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: 'マイページ',
      description: '',
    },
  },
  {
    // アカウント情報変更（プロフィール変更）
    path: '/mypage/profile/update',
    name: 'updateProfile',
    component: () => import('@/components/pages/UpdateProfilePage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: 'アカウント情報変更',
      description: '',
    },
  },
  {
    path: '/mypage/works/post',
    name: 'postWork',
    component: () => import('@/components/pages/PostWorkPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: '',
      description: '',
    },
  },
  {
    // 退会（アカウント削除）
    path: '/mypage/quit',
    name: 'quit',
    component: () => import('@/components/pages/QuitPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: 'アカウント削除',
      description: '',
    },
  },
  {
    path: '/mypage/works/edit/:workId',
    name: 'editWork',
    props: true,
    component: () => import('@/components/pages/PostWorkPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: '',
      description: '',
    },
  },
  {
    // マイページ作品一覧
    path: '/mypage/works',
    name: 'works',
    component: () => import('@/components/pages/OwnWorksPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: '作品',
      description: '',
    },
  },
  {
    // マイページお気に入り一覧
    path: '/mypage/favorites',
    name: 'favorites',
    component: () => import('@/components/pages/FavoritesPage/Index.vue'),
    meta: {
      requiresAuth: true,
      title: 'お気に入り',
      description: '',
    },
  },
  {
    // NotFound
    path: '/:catchAll(.*)',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: '404 NotFound',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwNotFound.vue'),
      },
    ],
  },
  {
    // メールアドレス変更時の確認OK
    path: '/verify/complete',
    name: 'emailVerifyComplete',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: 'メールアドレスの確認完了',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwVerifyComplete.vue'),
      },
    ],
  },
  {
    // メールアドレス変更時の確認NG
    path: '/verify/error',
    name: 'emailVerifyError',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: 'メールアドレスの確認失敗',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwVerifyError.vue'),
      },
    ],
  },
  {
    // NotFound
    path: '/404',
    name: '404',
    component: () => import('@/components/pages/StaticPage/Index.vue'),
    meta: {
      title: '404 NotFound',
      description: '',
    },
    children: [
      {
        path: '',
        component: () => import('@/components/pages/StaticPage/SwNotFound.vue'),
      },
    ],
  },
];

// ページ遷移時は常にTOPにスクロール
const scrollBehavior: RouterScrollBehavior = () => ({ top: 0, left: 0 });

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior,
});

router.beforeEach(async (to, from, next) => {
  let me = null;
  // Auth required ?
  if (!(to.matched.some((record) => record.meta.requiresAuth))) return next();
  let shouldLogin = false;
  console.log('requiresAuth');
  // has token?
  if (!localStorage.getItem('accessToken')) {
    return next({
      path: '/signin',
      query: {
        redirect: to.fullPath,
      },
    });
  }

  console.debug('exist accessToken');

  // User情報取得
  me = await MetadataService.me();
  // token enabled ?
  if (!me) {
    if (localStorage.getItem('accessToken') && localStorage.getItem('refreshToken')) {
      // accessTokenがある場合はrefresh
      const input: RefreshIndexPost = { refreshToken: localStorage.getItem('refreshToken') };
      const res = await AuthenticationService.refresh(input);
      if (res.response?.AccessToken) {
        console.log('refresh');
        localStorage.setItem('accessToken', res.response?.AccessToken);
      }
      me = await MetadataService.me();
      if (!me) {
        console.log('nothing me');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        shouldLogin = true;
        return next({
          path: '/signin',
          query: {
            redirect: to.fullPath,
          },
        });
      }
    } else {
      console.log('nothing me');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      shouldLogin = true;
      return next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
        },
      });
    }
  }
  // has Profile ?
  if (!me.registerProfileAt) {
    // プロフィール登録ページを表示
    return next({
      path: '/register',
      query: {
        redirect: to.fullPath,
      },
    });
  }

  Sentry.setUser({
    username: me.cognitoUsername,
    email: me.email,
  });

  return next();
});

/**
 * タイトルおよびDescriptionの書き換え
 * @param route
 */
const setupHeader = (route: any) => {
  if (route.meta.title) {
    document.title = `${route.meta.title} | スポットライト`;
  } else {
    document.title = 'スポットライト | 得意な工程で参加できるWEBTOONとマンガの投稿サイト';
  }
  if (route.meta && route.meta.description) {
    document.querySelector('meta[name=description]')?.setAttribute('content', route.meta.description as string);
  }
};

router.afterEach(async (to, from, next) => {
  setupHeader(to);
});

export default router;
